
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  CreditCard, 
  Search, 
  ShieldCheck, 
  AlertTriangle, 
  User, 
  Building, 
  ExternalLink, 
  CheckCircle, 
  XCircle,
  Zap,
  Info
} from 'lucide-react';

export const IBANView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [iban, setIban] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleVerify = () => {
    if (!iban) return;
    setIsVerifying(true);
    setTimeout(() => {
      const match = payeeName.toLowerCase().includes('global') || !payeeName;
      setResult({
        isValid: true,
        bank: 'Barclays Bank PLC',
        country: 'United Kingdom',
        payeeMatch: match ? 'Match' : 'Mismatch',
        accountStatus: 'Active',
        risks: []
      });
      setIsVerifying(false);
    }, 1800);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">IBAN Monitoring</h1>
            <p className="text-sm text-slate-500 font-medium tracking-tight">Real-time bank account validation & Confirmation of Payee (CoP).</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[10px] font-black uppercase tracking-widest">Swift-Network Live</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Verification Form */}
        <div className="lg:col-span-5 space-y-6">
           <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center">
                 <Search size={16} className="mr-2 text-blue-600" /> Account Audit
              </h3>
              
              <div className="space-y-4">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Account Holder Name (CoP)</label>
                    <div className="relative">
                       <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                       <input 
                         type="text" 
                         value={payeeName}
                         onChange={(e) => setPayeeName(e.target.value)}
                         className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" 
                         placeholder="e.g., Global Logistics Ltd" 
                       />
                    </div>
                 </div>

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">IBAN / Account Number</label>
                    <div className="relative">
                       <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                       <input 
                         type="text" 
                         value={iban}
                         onChange={(e) => setIban(e.target.value.toUpperCase())}
                         className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" 
                         placeholder="GB29 BARC 2039 44..." 
                       />
                    </div>
                 </div>

                 <button 
                   onClick={handleVerify}
                   disabled={isVerifying || !iban}
                   className="w-full py-4 bg-slate-900 text-white rounded-[1.5rem] text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center space-x-2 shadow-xl shadow-slate-200"
                 >
                   {isVerifying ? (
                     <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                   ) : (
                     <>
                        <ShieldCheck size={16} />
                        <span>Verify Account</span>
                     </>
                   )}
                 </button>
              </div>

              <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 flex items-start space-x-3">
                 <Info size={16} className="text-blue-600 mt-0.5" />
                 <p className="text-[10px] text-blue-700 leading-relaxed font-semibold uppercase tracking-tight">
                    Uses Modulus checking & SEPA Reachability APIs to confirm account status in 1.8s.
                 </p>
              </div>
           </div>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-7">
           {result ? (
             <div className="bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col animate-in zoom-in-95 duration-500">
                <div className="p-8 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                   <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${result.isValid ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                         {result.isValid ? <CheckCircle size={24} /> : <XCircle size={24} />}
                      </div>
                      <div>
                         <h4 className="text-lg font-black text-slate-900 leading-tight">Verification Successful</h4>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Swift-Response: 200 OK</p>
                      </div>
                   </div>
                   <span className="text-[10px] font-black bg-blue-600 text-white px-3 py-1 rounded-full uppercase tracking-widest">Valid IBAN</span>
                </div>
                
                <div className="p-8 flex-1 space-y-8">
                   <div className="grid grid-cols-2 gap-8">
                      <div>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Issuing Bank</p>
                         <div className="flex items-center space-x-2">
                            <Building size={16} className="text-slate-400" />
                            <span className="text-sm font-black text-slate-900">{result.bank}</span>
                         </div>
                      </div>
                      <div>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Payee Match (CoP)</p>
                         <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${result.payeeMatch === 'Match' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                           {result.payeeMatch}
                         </span>
                      </div>
                   </div>

                   <div className="bg-slate-900 rounded-[2rem] p-6 text-white relative overflow-hidden group">
                      <div className="relative z-10 flex items-center justify-between">
                         <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Account Health</p>
                            <p className="text-2xl font-black">{result.accountStatus}</p>
                         </div>
                         <Zap size={24} className="text-blue-400 animate-pulse" />
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600 rounded-full blur-[60px] opacity-10 translate-x-1/2 -translate-y-1/2"></div>
                   </div>

                   <div className="space-y-3">
                      <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-tight text-slate-500">
                         <span>Verification Metadata</span>
                         <span className="text-blue-600 hover:underline cursor-pointer flex items-center">Download Proof <ExternalLink size={10} className="ml-1" /></span>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 border-dashed space-y-2">
                         <div className="flex justify-between text-[10px] font-bold">
                            <span className="text-slate-400">COUNTRY</span>
                            <span className="text-slate-900">{result.country}</span>
                         </div>
                         <div className="flex justify-between text-[10px] font-bold">
                            <span className="text-slate-400">STRUCTURE</span>
                            <span className="text-slate-900">Valid ISO-13616</span>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
           ) : (
             <div className="h-full bg-white rounded-[3rem] border border-slate-200 border-dashed flex flex-col items-center justify-center text-slate-400 text-center space-y-4 p-12">
                <div className="p-6 bg-slate-50 rounded-full border border-slate-100"><CreditCard size={48} className="opacity-20" /></div>
                <div>
                   <p className="text-sm font-black uppercase tracking-widest text-slate-900 mb-1">Awaiting Data Entry</p>
                   <p className="text-xs font-medium max-w-xs leading-relaxed">Enter an IBAN or bank account number to trigger global reachability checks.</p>
                </div>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};
